import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useAuth } from '@/lib/use-auth';
import { ApiError } from '@/lib/api';
import { fetchPublicTutor } from '@/lib/tutor-api';
import { addToShortlist, removeFromShortlist, fetchMyParentProfile } from '@/lib/parent-api';
import { getMyBookings } from '@/lib/booking-api';
import { fetchTutorReviews } from '@/lib/review-api';
import { TutorProfileSkeleton } from './TutorProfileSkeleton';
import {
  DAYS_OF_WEEK,
  type DayOfWeek,
  type PublicTutorView,
} from '@/lib/schemas/tutor-profile';
import type { ChildResponse } from '@/lib/schemas/parent-profile';
import type { ReviewResponse } from '@/lib/schemas/review';
import { Button } from '@/components/ui/button';
import { SlotPickerModal } from '@/components/SlotPickerModal';
import { BookClassModal } from '@/components/BookClassModal';
import { BookingSuccessModal } from '@/components/BookingSuccessModal';
import {
  ArrowLeft,
  Star,
  Heart,
  Calendar,
  Clock,
  Briefcase,
  GraduationCap,
  Video,
  ShieldCheck,
  User,
  BookOpen,
  Plus
} from 'lucide-react';

const DAY_FULL: Record<DayOfWeek, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

function StarDisplay({ rating }: { rating: number }) {
  return (
    <div className="flex items-center text-amber-400 gap-0.5" aria-label={`${rating} stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${
            i < Math.round(rating)
              ? 'fill-amber-400 text-amber-400'
              : 'text-slate-300 dark:text-slate-700'
          }`}
        />
      ))}
    </div>
  );
}

function getYoutubeEmbedUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === 'youtu.be') {
      return `https://www.youtube.com/embed${parsed.pathname}`;
    }
    if (parsed.hostname.includes('youtube.com')) {
      const v = parsed.searchParams.get('v');
      if (v) return `https://www.youtube.com/embed/${v}`;
    }
  } catch {
    return null;
  }
  return null;
}

export function TutorViewPage() {
  const { id } = useParams<{ id: string }>();
  const { accessToken, user } = useAuth();
  const [tutor, setTutor] = useState<PublicTutorView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bookModalOpen, setBookModalOpen] = useState(false);
  const [children, setChildren] = useState<ChildResponse[]>([]);
  const [booked, setBooked] = useState(false);
  const [reviews, setReviews] = useState<ReviewResponse[]>([]);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successDetails, setSuccessDetails] = useState<{
    summaryMessage: string;
    tutorName: string;
    childName: string;
  } | null>(null);

  const handleBookingSuccess = useCallback((details: {
    summaryMessage: string;
    tutorName: string;
    childName: string;
  }) => {
    setSuccessDetails(details);
    setSuccessModalOpen(true);
  }, []);

  useEffect(() => {
    if (!accessToken || !id) return;
    let cancelled = false;
    setLoading(true);
    fetchPublicTutor(accessToken, id)
      .then((t) => {
        if (!cancelled) {
          setTutor(t);
          setLoading(false);
          // Set browser tab title dynamically
          document.title = `${t.displayName} | CosmoBud`;
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : 'Failed to load tutor');
          setLoading(false);
        }
      });

    if (id && accessToken) {
      fetchTutorReviews(accessToken, id)
        .then((r) => { if (!cancelled) setReviews(r); })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [accessToken, id]);

  // Load saved status from parent profile
  useEffect(() => {
    if (!accessToken || !id || user?.role === 'tutor') return;
    let cancelled = false;
    (async () => {
      try {
        const profile = await fetchMyParentProfile(accessToken);
        if (!cancelled) {
          setSaved(profile.shortlistedTutorIds.includes(id));
        }
      } catch {
        // Silently ignore errors loading saved status
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, id, user?.role]);

  // Load booked status from existing bookings
  useEffect(() => {
    if (!accessToken || !id || user?.role === 'tutor') return;
    let cancelled = false;
    (async () => {
      try {
        const bookings = await getMyBookings(accessToken);
        if (!cancelled) {
          const hasBooking = bookings.some((b) => b.tutorId === id);
          setBooked(hasBooking);
        }
      } catch {
        // Silently ignore errors loading bookings
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, id, user?.role]);

  const handleOpenPicker = useCallback(async () => {
    if (!accessToken) return;
    try {
      const profile = await fetchMyParentProfile(accessToken);
      setChildren(profile.children);
    } catch {
      setChildren([]);
    }
    setPickerOpen(true);
  }, [accessToken]);

  const handleOpenBookModal = useCallback(async () => {
    if (!accessToken) return;
    try {
      const profile = await fetchMyParentProfile(accessToken);
      setChildren(profile.children);
    } catch {
      setChildren([]);
    }
    setBookModalOpen(true);
  }, [accessToken]);

  const handleBookingComplete = useCallback(() => {
    setBooked(true);
  }, []);

  // Toggle Save to parent shortlist (addToShortlist & removeFromShortlist)
  const handleToggleSave = async () => {
    if (!accessToken || !id) return;
    setSaving(true);
    try {
      if (saved) {
        await removeFromShortlist(accessToken, id);
        setSaved(false);
      } else {
        await addToShortlist(accessToken, id);
        setSaved(true);
      }
    } catch {
      // silently ignore
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <TutorProfileSkeleton />;
  }

  if (error || !tutor) {
    return (
      <div className="max-w-3xl mx-auto py-6 space-y-4">
        <Link to={user?.role === 'tutor' ? '/' : '/tutors'} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors font-medium font-semibold cursor-pointer">
          <ArrowLeft className="h-4 w-4" />
          <span>{user?.role === 'tutor' ? 'Back to dashboard' : 'Back to search'}</span>
        </Link>
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50/50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-400"
        >
          {error ?? 'Tutor not found.'}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 py-2">
      {/* Back button link */}
      <div>
        <Link
          to={user?.role === 'tutor' ? '/' : '/tutors'}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors font-semibold cursor-pointer group"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          <span>{user?.role === 'tutor' ? 'Back to dashboard' : 'Back to search'}</span>
        </Link>
      </div>

      {/* 1. Header Card Banner */}
      <div className="relative overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
        {/* Profile Details Row */}
        <div className="p-6 flex flex-col md:flex-row md:items-start justify-between gap-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left">
            <div className="relative shrink-0">
              {tutor.photoUrl ? (
                <img
                  src={tutor.photoUrl}
                  alt={tutor.displayName}
                  className="h-20 w-20 rounded-full object-cover border border-slate-200 dark:border-slate-700 ring-2 ring-brand-500/10 shadow-sm"
                />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-violet-500 text-2xl font-bold text-white border border-slate-200 dark:border-slate-700 shadow-sm ring-2 ring-brand-500/10">
                  {tutor.displayName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                <h1 className="text-2xl font-black text-slate-900 dark:text-slate-100">{tutor.displayName}</h1>
                <div className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/30 px-2.5 py-0.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 select-none">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                  <span>Verified Educator</span>
                </div>
              </div>

              <p className="text-sm font-medium text-slate-600 dark:text-slate-400 max-w-xl">{tutor.headline}</p>

              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-1 pt-1 text-xs text-slate-500 dark:text-slate-400">
                {tutor.averageRating != null && (
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                    <span className="font-bold text-slate-800 dark:text-slate-200">
                      {tutor.averageRating.toFixed(1)}
                    </span>
                    <span className="text-slate-400 dark:text-slate-500">
                      ({reviews.length} review{reviews.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                )}
                
              </div>
            </div>
          </div>

          {/* Rate Display */}
          <div className="flex flex-col items-center md:items-end gap-2 shrink-0 self-center md:self-auto w-full md:w-auto">
            <div className="inline-flex items-center gap-2 bg-brand-50 dark:bg-slate-900 px-3.5 py-1.5 rounded-lg border border-brand-100 dark:border-slate-800 shadow-sm">
              <span className="text-xs font-extrabold text-brand-600 dark:text-brand-400 uppercase tracking-widest border-r border-brand-200 dark:border-slate-800 pr-2">
                Rate
              </span>
              <span className="text-base font-black text-brand-600 dark:text-brand-400">
                ${Math.round(tutor.hourlyRate.usdMinor / 100)}
                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 ml-0.5">/hr</span>
              </span>
            </div>
          </div>
        </div>

        {/* Actions bar - Hidden for tutors previewing their profile */}
        {user?.role !== 'tutor' && (
          <div className="border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-4 flex flex-col sm:flex-row gap-3 justify-center sm:justify-start items-stretch sm:items-center">
            <Button
              variant="outline"
              onClick={handleToggleSave}
              disabled={saving}
              className={`font-semibold text-xs h-10 border-slate-200 dark:border-slate-700 flex items-center justify-center gap-2 px-4 rounded-lg cursor-pointer w-full sm:w-auto ${
                saved ? 'bg-red-50 text-red-500 border-red-200 hover:bg-red-100 dark:bg-red-950/30 dark:border-red-900/40' : ''
              }`}
            >
              <Heart className={`h-4 w-4 ${saved ? 'fill-red-500 text-red-500' : ''}`} />
              <span>{saved ? 'Saved to shortlist' : 'Save to shortlist'}</span>
            </Button>

            <Button
              variant="outline"
              onClick={handleOpenPicker}
              disabled={booked}
              className="font-semibold text-xs h-10 border-slate-200 dark:border-slate-700 px-4 rounded-lg cursor-pointer w-full sm:w-auto flex items-center justify-center"
            >
              {booked ? 'Demo requested ✓' : 'Request free demo class'}
            </Button>

            <Button
              onClick={handleOpenBookModal}
              className="font-bold text-xs h-10 bg-brand-600 hover:bg-brand-700 dark:bg-brand-500 dark:hover:bg-brand-600 text-white shadow-sm sm:ml-auto w-full sm:w-auto px-5 rounded-lg cursor-pointer flex items-center justify-center gap-1.5"
            >
              <Plus className="h-4 w-4" />
              Book a Class
            </Button>
          </div>
        )}
      </div>

      {/* 2. Content Split Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Biography, Video, Credentials, Reviews */}
        <div className="col-span-1 lg:col-span-8 space-y-6">
          {/* Bio Section */}
          {tutor.bio && (
            <section className="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm space-y-3">
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <User className="h-5 w-5 text-brand-500" />
                <span>About {tutor.displayName}</span>
              </h2>
              <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400 whitespace-pre-line">
                {tutor.bio}
              </p>
            </section>
          )}

          {/* Demo Video Player Card */}
          {tutor.demoVideoUrl && (() => {
            const ytEmbedUrl = getYoutubeEmbedUrl(tutor.demoVideoUrl);
            return (
              <section className="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm space-y-3">
                <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Video className="h-5 w-5 text-brand-500" />
                  <span>Introduction Video</span>
                </h2>
                <div className="relative overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 group bg-slate-950 aspect-video flex items-center justify-center">
                  {ytEmbedUrl ? (
                    <iframe
                      src={ytEmbedUrl}
                      title="YouTube video player"
                      className="absolute inset-0 w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <>
                      {/* Visual decoration overlay */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-brand-900/40 via-transparent to-violet-900/30 opacity-70 group-hover:scale-105 transition-all duration-500" />
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 space-y-3 z-10">
                        <a
                          href={tutor.demoVideoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-slate-900 shadow-lg group-hover:scale-110 hover:bg-slate-100 transition-all cursor-pointer border-4 border-slate-900/20"
                        >
                          <Video className="h-7 w-7 text-brand-600 fill-brand-600 ml-0.5 animate-pulse" />
                        </a>
                        <div className="space-y-1">
                          <h3 className="font-bold text-white text-sm">Watch Class Demo</h3>
                          <p className="text-xs text-slate-300">Click to watch a short demo introduction of the classes</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </section>
            );
          })()}

          {/* Qualifications & Experience Timeline */}
          <section className="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm space-y-4">
            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-brand-500" />
              <span>Qualifications & Experience</span>
            </h2>

            <div className="flex gap-3 items-start p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
              <Briefcase className="h-5 w-5 text-brand-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">Teaching Experience</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {tutor.yearsExperience} year{tutor.yearsExperience === 1 ? '' : 's'} of teaching experience
                </p>
              </div>
            </div>

            {tutor.qualifications.length > 0 && (
              <div className="space-y-3.5 pl-1.5 pt-1">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Degrees & Credentials</h3>
                <ul className="relative border-l border-slate-200 dark:border-slate-700 pl-4 space-y-4 ml-2.5">
                  {tutor.qualifications.map((q, i) => (
                    <li key={i} className="relative">
                      {/* Timeline Dot */}
                      <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-brand-500 ring-4 ring-white dark:ring-slate-800" />
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-slate-800 dark:text-slate-200">
                            {q.degree} {q.field ? `in ${q.field}` : ''}
                          </span>
                          <span className="text-2xs font-semibold text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-950/30 border border-brand-100/10 px-2 py-0.5 rounded">
                            {q.year}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                          {q.institution}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
          {/* Reviews List */}
          {reviews.length > 0 && (
            <section className="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm space-y-4">
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Star className="h-5 w-5 text-brand-500 fill-brand-500" />
                <span>Parent Reviews ({reviews.length})</span>
              </h2>
              <ul className="space-y-4">
                {reviews.map((r) => (
                  <li key={r.id} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm space-y-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-1.5">
                        <StarDisplay rating={r.rating} />
                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200">{r.rating.toFixed(1)}</span>
                      </div>
                      <span className="text-2xs font-medium text-slate-400">
                        {new Date(r.createdAt).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                    </div>
                    {r.comment && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed italic">
                        "{r.comment}"
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* Right Column: Availability Calendar & Badge Filters */}
        <div className="col-span-1 lg:col-span-4 space-y-6 lg:sticky lg:top-6">
          {/* Availability Card */}
          <div className="bg-white dark:bg-slate-800 p-5 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm space-y-4">
            <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2 border-b border-slate-100 dark:border-slate-700 pb-3">
              <Calendar className="h-4.5 w-4.5 text-brand-500" />
              <span>Weekly Availability</span>
            </h2>
            <div className="text-xs text-slate-500 dark:text-slate-400 space-y-2">
              <div className="flex justify-between items-center py-1 border-b border-slate-100 dark:border-slate-700">
                <span>Weekly hours:</span>
                <span className="font-bold text-slate-800 dark:text-slate-200">{tutor.weeklyAvailabilityHours} hrs/week</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-100 dark:border-slate-700">
                <span>Timezone:</span>
                <span className="font-bold text-slate-800 dark:text-slate-200">{tutor.homeTz}</span>
              </div>
            </div>
            {tutor.availability.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Active Schedule</h3>
                <ul className="space-y-2">
                  {DAYS_OF_WEEK.filter((day) =>
                    tutor.availability.some((a) => a.day === day),
                  ).map((day) => (
                    <li key={day} className="flex items-start justify-between text-xs py-1.5 border-b border-slate-100 dark:border-slate-700">
                      <span className="font-bold text-slate-700 dark:text-slate-300">{DAY_FULL[day]}</span>
                      <div className="flex flex-wrap justify-end gap-1 max-w-[70%]">
                        {tutor.availability
                          .filter((a) => a.day === day)
                          .map((a, idx) => (
                            <span key={idx} className="inline-flex items-center gap-1 rounded bg-brand-50 dark:bg-slate-900 border border-brand-100/10 px-2 py-0.5 text-3xs font-semibold text-brand-700 dark:text-brand-400">
                              <Clock className="h-2.5 w-2.5" />
                              {a.startLocal}–{a.endLocal}
                            </span>
                          ))}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Subjects & Grades badge card */}
          <div className="bg-white dark:bg-slate-800 p-5 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm space-y-4">
            <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2 border-b border-slate-100 dark:border-slate-700 pb-3">
              <BookOpen className="h-4.5 w-4.5 text-brand-500" />
              <span>Subjects & Grades</span>
            </h2>
            <div className="space-y-3">
              <div className="space-y-2">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Subjects</h3>
                <div className="flex flex-wrap gap-1.5">
                  {tutor.subjects.map((s) => (
                    <span
                      key={s}
                      className="rounded-lg bg-violet-50 dark:bg-violet-900 px-2.5 py-1 text-xs font-semibold text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-850"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
              <div className="space-y-2 pt-1">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Grades</h3>
                <div className="flex flex-wrap gap-1.5">
                  {tutor.gradeLevels.map((g) => (
                    <span
                      key={g}
                      className="rounded-lg bg-brand-50 dark:bg-brand-900 px-2.5 py-1 text-xs font-semibold text-brand-700 dark:text-brand-400 border border-brand-100/20"
                    >
                      {g}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking and success modals */}
      {tutor && accessToken && (
        <SlotPickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          tutorId={id ?? ''}
          tutorName={tutor.displayName}
          homeTz={tutor.homeTz}
          availability={tutor.availability}
          children={children}
          accessToken={accessToken}
          onBookingComplete={handleBookingComplete}
        />
      )}

      {tutor && accessToken && (
        <BookClassModal
          open={bookModalOpen}
          onClose={() => setBookModalOpen(false)}
          accessToken={accessToken}
          childOptions={children}
          initialTutor={tutor}
          onBooked={handleBookingComplete}
          onSuccess={handleBookingSuccess}
        />
      )}

      {successDetails && (
        <BookingSuccessModal
          open={successModalOpen}
          onClose={() => {
            setSuccessModalOpen(false);
            setSuccessDetails(null);
          }}
          summaryMessage={successDetails.summaryMessage}
          tutorName={successDetails.tutorName}
          childName={successDetails.childName}
        />
      )}
    </div>
  );
}
